export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export type DeepRequired<T> = {
    [P in keyof T]-?: T[P] extends object ? DeepRequired<T[P]> : T[P];
  };

/**
 * union type consisting of all numbers from `0` to `N` (including `0`, excluding `N`)
 *
 * @example
 * ```
 * const aNumberBetween0And9: AnyNumberUpTo<10>
 * // type of `aNumberBetween0And9` will be: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
 *
 * const aNumberBetween0And9: AnyNumberUpTo<10> = 9; // => ok
 * const aNumberBetween0And9: AnyNumberUpTo<10> = 10; // => nok: Type '10' is not assignable to type '0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9'
 * ```
 */
export type AnyNumberUpTo<N extends number, Acc extends number[] = []> = Acc['length'] extends N
  ? Acc[number]
  : AnyNumberUpTo<N, [...Acc, Acc['length']]>

/**
 * union type consisting of all numbers from `M` to `N` (including `M`, excluding `N`)
 *
 * @example
 * ```
 * const aNumberBetween2And9: AnyNumberBetween<2, 10>
 * // type of `aNumberBetween2And9` will be: 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
 *
 * const aNumberBetween2And9: AnyNumberBetween<2, 10> = 9; // => ok
 * const aNumberBetween2And9: AnyNumberBetween<2, 10> = 1; // => nok: Type '1' is not assignable to type '2 | 3 | 4 | 5 | 6 | 7 | 8 | 9'
 * ```
 */
export type AnyNumberBetween<M extends number, N extends number> = Exclude<AnyNumberUpTo<N>, AnyNumberUpTo<M>>

/**
 * make some properties in `T` optional (as opposed to Partial which makes all of them optional)
 *
 * @example
 * ```
 * type T = {
 *   foo: number;
 *   bar: string;
 *   baz: boolean;
 * }
 *
 * type MissingFoo = OptionalKeys<T, 'foo'>
 * type MissingFooAndBar = OptionalKeys<T, 'foo' | 'bar'>
 *
 * const wrong1: MissingFoo = {}; // error: bar and baz are missing
 * const correct1: MissingFoo = { bar: 'bar', baz: true }; // ok
 *
 * const wrong2: MissingFooAndBar = {}; // error: baz is missing
 * const correct2: MissingFooAndBar = { baz: true }; // ok
 * ```
 */
export type OptionalKeys<T, K extends keyof T> = Partial<Pick<T, K>> & Omit<T, K>

/**
 * make some properties in `T` mandatory (non-optional)
 *
 * @example
 * ```
 * type Round = {
 *   name: string;
 *   status?: string;
 *   winningBalls?: number[];
 * }
 *
 * type RoundWithStatus = MandatoryKeys<Round, 'status'>;
 *
 * // RoundWithStatus will be:
 * // {
 * //  name: string;
 * //  status: string;           // this is mandatory now
 * //  winningBalls?: number[];
 * // }
 *
 * type RoundWithStatusAndBalls = MandatoryKeys<Round, 'status' | 'winningBalls'>;
 *
 * // RoundWithStatusAndBalls will be:
 * // {
 * //  name: string;
 * //  status: string;           // this is mandatory now
 * //  winningBalls: number[];   // this is mandatory now
 * // }
 *
 * // note that if type is declared like this:
 * type AlernativeRound = {
 *   name: string;
 *   status: string | undefined;
 * }
 *
 * type IdenticalAlernativeRound = MandatoryKeys<AlernativeRound, 'status'>;
 *
 * // IdenticalAlernativeRound will be identical to AlernativeRound:
 * // {
 * //  name: string;
 * //  status: string | undefined;   // not just string
 * // }
 * ```
 */
export type MandatoryKeys<T, K extends keyof T> = T & { [P in K]-?: T[P] }

/**
 * make some properties in `T` non nullish (not `null` and not `undefined`)
 *
 * @example
 * ```
 * type Round = {
 *   name: string;
 *   status?: string | null;
 *   winningBalls: undefined | number[];
 * }
 *
 * type RoundWithoutNullish = NonNullableKeys<Round, 'status' | 'winningBalls'>;
 *
 * // RoundWithoutNullish will be:
 * // {
 * //  name: string;
 * //  status: string;           // this can't be undefined or null anymore
 * //  winningBalls: number[];   // this can't be undefined anymore
 * // }
 * ```
 */
export type NonNullableKeys<T, K extends keyof T> = T & { [P in K]-?: NonNullable<T[P]> }

/**
 * get a union type of all values in T
 *
 * @example
 * ```
 * const obj = {
 *  foo: 1,
 *  bar: 'bar',
 *  baz: true,
 * }
 *
 * type Values = ValueOf<typeof obj>
 *
 * // type `Values` will be: number | string | boolean
 * ```
 */
export type ValueOf<T> = T[keyof T]

/**
 * transform a string type from snake_case to CamelCase
 *
 * @example
 * ```
 * let a: ToCamelCase<'this_will_be_camel_cased'>; // type of a is "thisWillBeCamelCased"
 * let b: ToCamelCase<'table_i_d'>; // type of b is "tableID"
 * let c: ToCamelCase<'this__w_'>; // type of c is "thisW", note that multiple consecutive '_' and final '_' are supported!
 * ```
 */
export type ToCamelCase<S extends string> =
  S extends `${infer Head}_${infer Tail}`
    ? `${Head}${Capitalize<ToCamelCase<Tail>>}`
    : S;

/**
 * infer return type of a function or return the type itself
 *
 * @example
 * ```
 * type NumberFunction = (value: any) => number;
 * type Example1 = ExtractReturnType<NumberFunction>;
 * // type of `Example1` will be: number
 *
 * type Example2 = ExtractReturnType<string>;
 * // type of `Example2` will be: string
 *
 * type Example3 = ExtractReturnType<boolean>;
 * // type of `Example3` will be: boolean
 *
 * type Example4 = ExtractReturnType<undefined>;
 * // type of `Example4` will be: undefined
 *
 * type Example5 = ExtractReturnType<null>;
 * // type of `Example5` will be: null
 *
 * type Example6 = ExtractReturnType<object>;
 * // type of `Object` will be: object
 * ```
 */
export type ExtractReturnType<T> = T extends (value: any) => infer R ? R : T;

/**
 * a type to expand the nested object properties (Intelisense)
 *
 * @note
 *
 * this is not mandatory to use, but it helps to see the expanded deep nested object properties in (e.g. camelCase) format in the editor
 *
 * @example
 * ```
 * // Without ExpandRecursive
 * type NestedObject = {
 *  userInfo: {
 *   first_name: string;
 *   last_name: string;
 *   address_info: {
 *     street: string;
 *     city: string;
 *     zip_code: string;
 *   }
 * }
 *
 * // With ExpandRecursive
 * type ExpandedNestedObject = ExpandRecursive<{
 *  userInfo: {
 *    firstName: string;
 *    lastName: string;
 *    addressInfo: {
 *      street: string;
 *      city: string;
 *      zipCode: string;
 *    }
 *  }
 * }>
 * ```
 */
export type ExpandRecursive<T> = T extends Record<string, unknown>
  ? T extends infer O
    ? { [K in keyof O]: ExpandRecursive<O[K]> }
    : never
  : T;

/**
 * recursively transform object properties from snake_case to camelCase and infer return types of the properties
 *
 * @example
 * ```
 * type CamelizedObjectProperties = CamelizedObjectProps<{
 *   snake_one: number;
 *   snake_two: number;
 *   nested_object: {
 *     snake_five: number;
 *   }
 * }>
 *
 * // type of `CamelizedObjectProperties` will be:
 * // {
 * //   snakeOne: number;
 * //   snakeTwo: number;
 * //   nestedObject: {
 * //     snakeFive: number;
 * //   }
 * // }
 * ```
 */
export type CamelizeObjectProps<T extends Record<string, unknown>> = ExpandRecursive<{
  [K in keyof T as ToCamelCase<K & string>]: T[K] extends Record<string, unknown>
    ? CamelizeObjectProps<T[K]>
    : ExtractReturnType<T[K]>;
}>;

/** type guard that, when passed, guarantees that `object` has key `key` (note that value of `key` can be nullish) */
export const hasKey = <T extends object>(
  object: T,
  key: string | number | symbol,
): key is keyof T => key in object;

/** type guard that, when passed, guarantees that the existing key `key` of `object` is different than `null` and `undefined` */
export const hasNonNullishKey = <U, V extends keyof U>(
  object: U,
  key: V
): object is NonNullableKeys<U, V> => object[key] != null;

/**
 * similar to `Omit<Type, Keys>`, but works for keys deeply embedded
 * @example
 * ```
 * type Object = {
 *   one: number;
 *   two: {
 *     a: string;
 *     b: boolean;
 *   },
 *   three: symbol;
 * };
 *
 * type DeepOmitted = DeepOmit<Object, 'one' | 'two.a'>;
 *
 * // DeepOmitted will be:
 * // {
 * //   two: {
 * //       b: boolean;
 * //   };
 * //   three: symbol;
 * // }
 * ```
 */
export type DeepOmit<T, K extends PropertyKey> = {
  [P in keyof T as P extends K ? never : P]:
  DeepOmit<T[P], K extends `${Exclude<P, symbol>}.${infer R}` ? R : never>
} extends infer O ? { [P in keyof O]: O[P] } : never;
